import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import Async from 'components/Async';
import IntegrityAdvocate from 'components/IntegrityAdvocate';
import Page from 'components/Page';
import useNotification from 'hooks/useNotification';
import store from 'store';

import { useServiceRouter } from '@common/components/ServiceRouter';
import Tabs from '@common/components/Tabs';
import { LESSON_TYPE } from '@common/constants/LessonsType';
import { appId } from '@common/constants/integrityadvocateserver';
import isDatePassed from '@common/helpers/isDatePassed';

import useLessonLogic from '../hooks/useLessonLogic';
import LessonSection from './LessonSection';
import RulesModalWrapper from './RulesModalWrapper';
import LessonTimePlaceholder from './TimePlaceholder';

function LessonPage() {
	const { t } = useTranslation();

	const notificate = useNotification();
	const { lessonModel, goBack } = useLessonLogic();

	const { checkService } = useServiceRouter();

	const isDeadlinePassed = isDatePassed(lessonModel?.schedule_lesson?.deadline);

	const title = lessonModel.lesson_info?.name
		? `${lessonModel.lesson_info?.program?.name} / ${lessonModel.schedule_lesson?.name || lessonModel.lesson_info?.name}`
		: t('shared.back');

	const isTestLesson = LESSON_TYPE[lessonModel?.lesson_info?.lessonType?.id] === 'Test';
	useEffect(() => {
		if (isDeadlinePassed) {
			const key = notificate('error', t('programs.lessons.deadlineExpired'), {
				autoHideDuration: null,
				dismissible: true,
			});

			return () => {
				notificate.close(key);
			};
		}
	}, [isDeadlinePassed]);

	return (
		<Page
			type="list"
			isLoading={lessonModel.loading}
			title={!lessonModel.loading ? title : null}
			onGoBack={goBack}
			sx={{ backdropFilter: 'unset' }}
		>
			<RulesModalWrapper lessonModel={lessonModel} goBack={goBack} isTestLesson={isTestLesson}>
				{!lessonModel.lesson_info?.program ? (
					<Async loading={!lessonModel.lessonStartDate}>
						<LessonTimePlaceholder lessonModel={lessonModel} />
					</Async>
				) : (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1 }}>
						<Tabs
							tabs={[
								...(lessonModel.lesson_info?.classwork?.is_enabled
									? [
											{
												label: t('tabs.classWork'),
												element: <LessonSection lesson={lessonModel} workType="classwork" />,
											},
										]
									: []),
								...(lessonModel.lesson_info?.homework?.is_enabled
									? [
											{
												label: checkService('international') ? 'ASSIGNMENT' : t('tabs.homeWork'),
												element: <LessonSection lesson={lessonModel} workType="homework" />,
											},
										]
									: []),
							]}
						/>
						{checkService('international') &&
							isTestLesson &&
							store.profile.student &&
							lessonModel.gradebook.is_done === 0 && (
								<IntegrityAdvocate
									appId={appId}
									participantIdentifier={store.profile.student.id}
									courseId={lessonModel?.schedule_program?.id}
									participantFirstName={store.profile.first_name}
									participantLastName={store.profile.last_name}
									participantEmail={store.profile.email}
									activityId="1"
								/>
							)}
					</Box>
				)}
			</RulesModalWrapper>
		</Page>
	);
}

export default observer(LessonPage);
